import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Upload, Loader2, Trash2, Check, AlertCircle, FileText, FolderUp, Search, ChevronDown, X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useIntelligence } from '../contexts';
import api from '../../services/api';
import type { StagingQueueItem, Protocol } from '../types';
import { DOCUMENT_TYPES } from '../types';
import { getConfidenceColor, getConfidenceBgColor } from '../types/staging';
import { StagingUploader } from '../components/staging/StagingUploader';
import { PDFThumbnail } from '../components/staging/PDFThumbnail';
import { PageHeader } from '../../ui';

export function StagingQueue() {
  const { currentSite, user } = useAuth();
  const { protocols } = useIntelligence();
  const [items, setItems] = useState<StagingQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [approving, setApproving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadItems = useCallback(async () => {
    if (!currentSite?.id) return;
    setLoading(true);
    try {
      const response = await api.get('/api/intelligence/staging', {
        params: { status: 'pending' }
      });
      setItems(response.data || []);
    } catch (error) {
      console.error('Error loading staging queue:', error);
    } finally {
      setLoading(false);
    }
  }, [currentSite?.id]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleUpload = async (files: File[]) => {
    if (!currentSite?.id || files.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      const response = await api.post('/api/intelligence/staging/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.errors?.length > 0) {
        console.warn('Some files failed to upload:', response.data.errors);
      }

      await loadItems();
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateItem = async (id: string, updates: Partial<StagingQueueItem>) => {
    try {
      await api.patch(`/api/intelligence/staging/${id}`, updates);
      setItems(prev => prev.map(item =>
        item.id === id ? { ...item, ...updates } : item
      ));
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  const handleDeleteItem = async (id: string) => {
    if (!window.confirm('Remove this file from the staging queue?')) return;

    try {
      await api.delete(`/api/intelligence/staging/${id}`);
      setItems(prev => prev.filter(item => item.id !== id));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Remove ${selectedIds.size} file(s) from the staging queue?`)) return;

    try {
      await Promise.all(
        Array.from(selectedIds).map(id => api.delete(`/api/intelligence/staging/${id}`))
      );
      setItems(prev => prev.filter(item => !selectedIds.has(item.id)));
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error deleting items:', error);
    }
  };

  const handleApproveSelected = async () => {
    if (selectedIds.size === 0) return;

    // Check if all selected items have required fields
    const selectedItems = items.filter(item => selectedIds.has(item.id));
    const incomplete = selectedItems.filter(item => !item.document_type);

    if (incomplete.length > 0) {
      alert(`Please set document type for all files before approving. ${incomplete.length} file(s) are missing document type.`);
      return;
    }

    setApproving(true);
    try {
      const response = await api.post('/api/intelligence/staging/approve', {
        item_ids: Array.from(selectedIds)
      });

      if (response.data.errors?.length > 0) {
        console.warn('Some items failed to approve:', response.data.errors);
        alert(`${response.data.approved} file(s) approved, ${response.data.failed} failed.`);
      }

      // Remove approved items from list
      setItems(prev => prev.filter(item => !selectedIds.has(item.id)));
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error approving items:', error);
    } finally {
      setApproving(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(item => item.id)));
    }
  };

  const isItemComplete = (item: StagingQueueItem) => {
    return !!item.document_type;
  };

  const selectedCount = selectedIds.size;
  const completeCount = items.filter(item => selectedIds.has(item.id) && isItemComplete(item)).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-primary-600 dark:text-primary-400" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <PageHeader
        className="mb-6"
        title="Upload Queue"
        icon={FolderUp}
        subtitle="Upload files, review metadata, and approve for processing"
      />

      {/* Uploader */}
      <StagingUploader onUpload={handleUpload} uploading={uploading} />

      {/* Queue */}
      {items.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center mt-6 dark:bg-gray-800 dark:border-gray-700">
          <FolderUp size={48} className="mx-auto text-gray-300 mb-4 dark:text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">No files in queue</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Upload files above to start the review process
          </p>
        </div>
      ) : (
        <>
          {/* Batch Actions Bar */}
          <div className="bg-white rounded-t-xl border border-b-0 border-gray-200 px-4 py-3 mt-6 flex items-center justify-between dark:bg-gray-800 dark:border-gray-700">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedIds.size === items.length && items.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                />
                <span className="text-sm text-gray-600 dark:text-gray-300">Select All</span>
              </label>
              {selectedCount > 0 && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedCount} selected
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedCount > 0 && (
                <>
                  <button
                    onClick={handleDeleteSelected}
                    className="px-3 py-1.5 text-sm font-medium text-red-600 rounded-lg transition-colors duration-150 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-red-400 dark:hover:bg-red-900/20"
                  >
                    <Trash2 size={16} className="inline mr-1" />
                    Delete Selected
                  </button>
                  <button
                    onClick={handleApproveSelected}
                    disabled={approving || completeCount === 0}
                    className="px-4 py-1.5 text-sm font-medium bg-primary-600 text-white rounded-lg transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {approving ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Check size={16} />
                    )}
                    Approve Selected ({completeCount})
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-b-xl border border-gray-200 overflow-x-auto dark:bg-gray-800 dark:border-gray-700">
            <table className="w-full table-fixed">
              <thead className="bg-gray-50 border-b border-gray-200 dark:bg-gray-800/50 dark:border-gray-700">
                <tr>
                  <th className="w-12 px-4 py-3"></th>
                  <th className="w-20 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Preview</th>
                  <th className="w-[280px] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Filename</th>
                  <th className="w-[160px] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Type</th>
                  <th className="w-[240px] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Protocol</th>
                  <th className="w-16 px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Ready</th>
                  <th className="w-14 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {items.map((item) => (
                  <StagingQueueRow
                    key={item.id}
                    item={item}
                    protocols={protocols}
                    selected={selectedIds.has(item.id)}
                    onSelect={() => toggleSelect(item.id)}
                    onUpdate={(updates) => handleUpdateItem(item.id, updates)}
                    onDelete={() => handleDeleteItem(item.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// Row component
interface StagingQueueRowProps {
  item: StagingQueueItem;
  protocols: Protocol[];
  selected: boolean;
  onSelect: () => void;
  onUpdate: (updates: Partial<StagingQueueItem>) => void;
  onDelete: () => void;
}

function StagingQueueRow({ item, protocols, selected, onSelect, onUpdate, onDelete }: StagingQueueRowProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    // Fetch preview URL
    const fetchPreview = async () => {
      try {
        const response = await api.get(`/api/intelligence/staging/${item.id}/preview-url`);
        setPreviewUrl(response.data.url);
      } catch (error) {
        console.error('Error fetching preview URL:', error);
      }
    };
    fetchPreview();
  }, [item.id]);

  const isComplete = !!item.document_type;
  const confidence = item.suggestion_confidence;

  // Determine which protocol to show (user-selected or AI-suggested)
  const displayProtocolId = item.protocol_id || item.suggested_protocol_id;
  const displayProtocol = displayProtocolId
    ? protocols.find(p => p.id === displayProtocolId)
    : null;

  return (
    <tr className={`transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50 ${selected ? 'bg-primary-50 dark:bg-primary-900/20' : ''}`}>
      {/* Checkbox */}
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
        />
      </td>

      {/* Preview */}
      <td className="px-4 py-3">
        {item.mime_type === 'application/pdf' && previewUrl ? (
          <PDFThumbnail url={previewUrl} />
        ) : (
          <div className="w-16 h-20 bg-gray-100 rounded flex items-center justify-center dark:bg-gray-700">
            <FileText size={24} className="text-gray-400" />
          </div>
        )}
      </td>

      {/* Filename */}
      <td className="px-4 py-3 overflow-hidden">
        <div className="min-w-0">
          <p className="font-medium text-gray-900 truncate dark:text-gray-100" title={item.original_filename}>
            {item.original_filename}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {item.page_count ? `${item.page_count} pages` : ''}
            {item.page_count && item.file_size ? ' · ' : ''}
            {item.file_size ? formatFileSize(item.file_size) : ''}
          </p>
        </div>
      </td>

      {/* Document Type */}
      <td className="px-4 py-3">
        <select
          value={item.document_type || ''}
          onChange={(e) => onUpdate({ document_type: e.target.value || null })}
          className={`w-full text-sm border rounded-lg px-2 py-1.5 truncate text-gray-900 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-100 ${
            item.document_type
              ? 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-700'
              : 'border-yellow-400 bg-yellow-50 dark:border-yellow-600 dark:bg-yellow-900/20'
          }`}
        >
          <option value="">Select type...</option>
          {DOCUMENT_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </td>

      {/* Protocol */}
      <td className="px-4 py-3 overflow-hidden">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <SearchableProtocolSelect
                protocols={protocols}
                value={item.protocol_id || null}
                onChange={(protocolId) => onUpdate({
                  protocol_id: protocolId,
                  scope: protocolId ? 'protocol' : 'site'
                })}
                suggestedProtocolId={item.suggested_protocol_id}
                confidence={confidence}
              />
            </div>
            {!item.protocol_id && item.suggested_protocol_id && confidence !== null && (
              <span
                className={`text-xs font-medium flex-shrink-0 ${getConfidenceColor(confidence)}`}
                title={item.suggestion_reason || 'AI suggestion'}
              >
                {Math.round(confidence * 100)}%
              </span>
            )}
          </div>
          {item.suggestion_reason && !item.protocol_id && (
            <p className="text-xs text-gray-500 mt-1 truncate dark:text-gray-400" title={item.suggestion_reason}>
              {item.suggestion_reason}
            </p>
          )}
        </div>
      </td>

      {/* Ready indicator */}
      <td className="px-4 py-3 text-center">
        {isComplete ? (
          <Check size={18} className="text-green-600 mx-auto dark:text-green-400" />
        ) : (
          <AlertCircle size={18} className="text-yellow-500 mx-auto dark:text-yellow-400" />
        )}
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <button
          onClick={onDelete}
          className="p-1.5 text-gray-400 transition-colors duration-150 hover:text-red-600 rounded dark:hover:text-red-400"
          title="Remove from queue"
        >
          <Trash2 size={16} />
        </button>
      </td>
    </tr>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

// Searchable Protocol Dropdown
interface SearchableProtocolSelectProps {
  protocols: Protocol[];
  value: string | null;
  onChange: (protocolId: string | null) => void;
  suggestedProtocolId?: string | null;
  confidence?: number | null;
}

function SearchableProtocolSelect({
  protocols,
  value,
  onChange,
  suggestedProtocolId,
  confidence,
}: SearchableProtocolSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sort protocols alphabetically by protocol_number
  const sortedProtocols = [...protocols].sort((a, b) =>
    (a.protocol_number || '').localeCompare(b.protocol_number || '')
  );

  // Filter protocols based on search
  const filteredProtocols = sortedProtocols.filter((p) => {
    const searchLower = search.toLowerCase();
    return (
      (p.protocol_number?.toLowerCase() || '').includes(searchLower) ||
      (p.study_nickname?.toLowerCase() || '').includes(searchLower) ||
      (p.sponsor_name?.toLowerCase() || '').includes(searchLower)
    );
  });

  const selectedProtocol = value ? protocols.find((p) => p.id === value) : null;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (protocolId: string | null) => {
    onChange(protocolId);
    setIsOpen(false);
    setSearch('');
  };

  const isSuggested = !value && suggestedProtocolId;

  return (
    <div ref={containerRef} className="relative">
      {/* Selected Value / Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full text-sm border rounded-lg px-2 py-1.5 text-left flex items-center justify-between gap-1 text-gray-900 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-100 ${
          isSuggested
            ? getConfidenceBgColor(confidence ?? null)
            : 'border-gray-300 bg-white hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600'
        }`}
      >
        <span className="truncate">
          {selectedProtocol
            ? `${selectedProtocol.protocol_number}${selectedProtocol.study_nickname ? ` - ${selectedProtocol.study_nickname}` : ''}`
            : 'None (Site-level)'}
        </span>
        <ChevronDown size={14} className="flex-shrink-0 text-gray-400" />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg dark:bg-gray-800 dark:border-gray-700">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-100 dark:border-gray-700">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search protocols..."
                className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 transition-colors duration-150 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Options */}
          <div className="max-h-60 overflow-y-auto">
            {/* None option */}
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className={`w-full text-left px-3 py-2 text-sm transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                !value ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400' : 'text-gray-700 dark:text-gray-200'
              }`}
            >
              None (Site-level)
            </button>

            {filteredProtocols.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center dark:text-gray-400">
                No protocols found
              </div>
            ) : (
              filteredProtocols.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handleSelect(p.id)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                    value === p.id ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400' : 'text-gray-700 dark:text-gray-200'
                  }`}
                >
                  <div className="font-medium truncate">{p.protocol_number}</div>
                  {(p.study_nickname || p.sponsor_name) && (
                    <div className="text-xs text-gray-500 truncate dark:text-gray-400">
                      {p.study_nickname}
                      {p.study_nickname && p.sponsor_name ? ' · ' : ''}
                      {p.sponsor_name}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StagingQueue;
